import React, { Suspense, useEffect } from "react";
import { graphql, PreloadedQuery, usePreloadedQuery, useQueryLoader } from "react-relay";

import IconButton from "@mui/material/IconButton";
import Skeleton from "common/DS/Skeleton";
import Stack from "@mui/material/Stack";

import { ContactInfoPageQuery as ContactInfoPageQueryType } from "__generated__/ContactInfoPageQuery.graphql";

import { CardLevelOne } from "common/DS/ElevatedCard";
import { ChevronLeftIcon } from "common/DS/Icons";
import HubPageWrapper from "hub_two_point_o/HubPageWrapper";

import ContactInfoField from "./ContactInfoField";

const ContactInfoPageQuery = graphql`
    query ContactInfoPageQuery {
        me {
            id
            email
            phone
        }
        contactInfo {
            id
            nodes {
                id
                type
                value
                state
            }
        }
    }
`;

type Props = {
    queryRef: PreloadedQuery<ContactInfoPageQueryType>;
};

const ContactInfoPageShell = ({ children }: { children: React.ReactNode }) => (
    <HubPageWrapper>
        <CardLevelOne className="hub__login" style={{ maxWidth: 520 }}>
            <div className="hub__login-title flex flex-ac gap-xs">
                <IconButton
                    component="a"
                    href="/hub"
                    aria-label="Back to hub"
                    sx={{ p: 0, color: "inherit" }}
                >
                    <ChevronLeftIcon sx={{ fontSize: 48 }} />
                </IconButton>
                Contact Info
            </div>
            <Stack spacing={3} className="w100">
                {children}
            </Stack>
        </CardLevelOne>
    </HubPageWrapper>
);

const ContactInfoPageLoading = () => (
    <ContactInfoPageShell>
        <Skeleton height={56} />
        <Skeleton height={56} />
    </ContactInfoPageShell>
);

const ContactInfoPageContent = ({ queryRef }: Props) => {
    const { me, contactInfo } = usePreloadedQuery(ContactInfoPageQuery, queryRef);

    const verifiedEmail = contactInfo.nodes.find(
        (n) => n.type === "EMAIL" && n.state === "VERIFIED",
    )?.value;
    const verifiedPhone = contactInfo.nodes.find(
        (n) => n.type === "PHONE" && n.state === "VERIFIED",
    )?.value;

    return (
        <ContactInfoPageShell>
            <ContactInfoField
                type="EMAIL"
                label="Email"
                initialValue={me.email}
                verifiedInitial={verifiedEmail}
            />
            <ContactInfoField
                type="PHONE"
                label="Phone"
                initialValue={me.phone}
                verifiedInitial={verifiedPhone}
            />
        </ContactInfoPageShell>
    );
};

const ContactInfoPageWrapper = () => {
    const [queryRef, loadQuery, disposeQuery] =
        useQueryLoader<ContactInfoPageQueryType>(ContactInfoPageQuery);

    useEffect(() => {
        loadQuery({});
        return () => {
            disposeQuery();
        };
    }, [loadQuery, disposeQuery]);

    if (!queryRef) return <ContactInfoPageLoading />;

    return <ContactInfoPageContent queryRef={queryRef} />;
};

const ContactInfoPage = () => (
    <Suspense fallback={<ContactInfoPageLoading />}>
        <ContactInfoPageWrapper />
    </Suspense>
);

export default ContactInfoPage;
